import { useRef, useState, type ReactNode } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Upload } from "lucide-react";
import {
  collapseEventRows,
  eventImportSpec,
  headersOf,
  validateRows,
  type ParsedEventImport,
} from "@event-planner/shared/imports";
import type {
  BulkCreateEventsResponse,
  BulkResolveEventsRowResult,
} from "@event-planner/shared";
import { downloadExcelTemplate, readExcelRows } from "../../utils/excel";
import {
  BulkImportResultTable,
  type BulkImportResultBucket,
} from "../../components/BulkImportResultTable";
import {
  bulkCreateEvents,
  bulkResolveEvents,
} from "../../services/eventService";

/**
 * Bulk Event import. Unlike the row-per-entity dialogs (Locations,
 * Sessions, etc.), the Event sheet uses a vertical key/value layout - one
 * row per field. The dialog collapses every parsed KV pair into a single
 * Event payload before sending to the resolve/write callables.
 *
 * Why this dialog doesn't reuse the shared ResolveAndImportDialog shell:
 * the shell is built around the per-row review pattern (one card per
 * source row). An Event sheet collapses to one entity, and forcing it
 * through a per-row review surface would mean either 17 redundant cards
 * (one per KV pair) or a fake "merged row" that breaks the shell's
 * line-number conventions. The bespoke dialog mirrors the shell's
 * 5-phase machine (picking / loading / reviewing / writing / done) but
 * renders a single Event summary card in the reviewing phase.
 */

interface ExcelEventImportDialogProps {
  open: boolean;
  onClose: () => void;
  /** Fires after a successful import so the parent can refetch. */
  onImported?: () => void;
}

type Phase = "picking" | "loading" | "reviewing" | "writing" | "done";

interface ReviewState {
  payload: ParsedEventImport;
  resolution: BulkResolveEventsRowResult;
  diagnostics: Array<{ lineNumber: number; message: string }>;
}

export function ExcelEventImportDialog({
  open,
  onClose,
  onImported,
}: ExcelEventImportDialogProps) {
  const [phase, setPhase] = useState<Phase>("picking");
  const [fileName, setFileName] = useState<string | null>(null);
  const [review, setReview] = useState<ReviewState | null>(null);
  const [writeResult, setWriteResult] =
    useState<BulkCreateEventsResponse | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const importedAnythingRef = useRef(false);

  function reset() {
    setPhase("picking");
    setFileName(null);
    setReview(null);
    setWriteResult(null);
    setParseError(null);
    setResolveError(null);
    setImportError(null);
  }

  function handleClose() {
    if (phase === "loading" || phase === "writing") return;
    if (importedAnythingRef.current && onImported) onImported();
    importedAnythingRef.current = false;
    reset();
    onClose();
  }

  async function handleFile(file: File) {
    setParseError(null);
    setResolveError(null);
    setFileName(file.name);
    setPhase("loading");

    let payload: ParsedEventImport;
    let diagnostics: Array<{ lineNumber: number; message: string }>;
    try {
      const { headers, rows: rawRows } = await readExcelRows<
        Record<string, string | undefined>
      >(file, { sheetName: eventImportSpec.sheetName });
      assertHeadersMatch(
        headers,
        headersOf(eventImportSpec),
        eventImportSpec.sheetName,
      );
      const parsed = validateRows(eventImportSpec, rawRows);
      const collapsed = collapseEventRows(parsed);
      diagnostics = collapsed.diagnostics;
      if (!collapsed.payload) {
        setParseError(
          collapsed.diagnostics
            .map((d) => `Row ${d.lineNumber}: ${d.message}`)
            .join("\n") || "Could not parse Event sheet.",
        );
        setPhase("picking");
        return;
      }
      payload = collapsed.payload;
    } catch (err) {
      setParseError(
        err instanceof Error
          ? `Failed to read file: ${err.message}`
          : "Failed to read file.",
      );
      setPhase("picking");
      return;
    }

    try {
      const response = await bulkResolveEvents({
        rows: [payloadToCallableRow(payload)],
      });
      setReview({ payload, resolution: response.results[0], diagnostics });
      setPhase("reviewing");
    } catch (err) {
      setResolveError(
        err instanceof Error
          ? `Failed to resolve: ${err.message}`
          : "Failed to resolve.",
      );
      setPhase("picking");
    }
  }

  async function handleImport() {
    if (!review) return;
    setPhase("writing");
    setImportError(null);
    try {
      const response = await bulkCreateEvents({
        rows: [payloadToCallableRow(review.payload)],
      });
      setWriteResult(response);
      importedAnythingRef.current = response.summary.created > 0;
      setPhase("done");
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "Bulk import failed.");
      setPhase("reviewing");
    }
  }

  const canImport =
    review !== null && review.resolution.status === "new";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      data-testid="excel-event-import-dialog"
    >
      <DialogTitle>Import Event from Excel</DialogTitle>
      <DialogContent dividers>
        {phase === "picking" && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Upload an Excel file (.xlsx) with one Event sheet in vertical
              key/value layout (one row per field: name, startDate, status, etc.,
              plus FAQ - question N / FAQ - answer N pairs). One event per file.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                onClick={async () =>
                  downloadExcelTemplate("event-import-template.xlsx", {
                    headers: headersOf(eventImportSpec),
                    exampleRows: [
                      eventImportSpec.example as Record<
                        string,
                        string | number | null
                      >,
                    ],
                    sheetName: "Event",
                  })
                }
                data-testid="excel-event-template-button"
              >
                Download Template
              </Button>
              <Button
                variant="contained"
                startIcon={<Upload size={16} />}
                component="label"
                data-testid="excel-event-upload-button"
              >
                Choose File
                <input
                  hidden
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFile(file);
                  }}
                />
              </Button>
            </Stack>
            {parseError && (
              <Alert severity="error">
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{ whiteSpace: "pre-wrap", m: 0 }}
                >
                  {parseError}
                </Typography>
              </Alert>
            )}
            {resolveError && <Alert severity="error">{resolveError}</Alert>}
          </Stack>
        )}

        {phase === "loading" && (
          <Stack spacing={2} alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
            <Typography variant="body2">Resolving...</Typography>
            <Typography variant="caption" color="text.secondary">
              {fileName}
            </Typography>
          </Stack>
        )}

        {phase === "reviewing" && review && (
          <Stack spacing={2}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography variant="body2">
                <strong>{fileName}</strong>: review one event
              </Typography>
              <Link
                component="button"
                onClick={reset}
                sx={{ ml: "auto", fontSize: "0.85rem" }}
              >
                Choose a different file
              </Link>
            </Stack>
            {importError && <Alert severity="error">{importError}</Alert>}
            <EventReviewCard review={review} />
            {review.diagnostics.length > 0 && (
              <Alert severity="warning">
                <Typography variant="body2" fontWeight={500}>
                  Diagnostics
                </Typography>
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                  {review.diagnostics.map((d, i) => (
                    <Typography key={i} variant="caption">
                      Row {d.lineNumber}: {d.message}
                    </Typography>
                  ))}
                </Stack>
              </Alert>
            )}
          </Stack>
        )}

        {phase === "writing" && (
          <Stack spacing={2} alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
            <Typography variant="body2">Importing event...</Typography>
          </Stack>
        )}

        {phase === "done" && writeResult && (
          <BulkImportResultTable
            buckets={buildEventBuckets(writeResult, review)}
          />
        )}
      </DialogContent>
      <DialogActions>
        {phase === "picking" && <Button onClick={handleClose}>Cancel</Button>}
        {phase === "reviewing" && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={!canImport}
              data-testid="excel-event-import-confirm"
            >
              Import Event
            </Button>
          </>
        )}
        {phase === "done" && (
          <Button variant="contained" onClick={handleClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function payloadToCallableRow(payload: ParsedEventImport) {
  return {
    name: payload.name,
    description: payload.description,
    startDate: payload.startDate,
    endDate: payload.endDate,
    timezone: payload.timezone,
    status: payload.status,
    venueLabel: payload.venueLabel,
    venueAddress: payload.venueAddress,
    heroImage: payload.heroImage,
    heroTitleOverride: payload.heroTitleOverride,
    faqs: payload.faqs,
  };
}

function EventReviewCard({ review }: { review: ReviewState }) {
  const { payload, resolution } = review;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="h6">{payload.name}</Typography>
          <StatusChip status={resolution.status} />
        </Stack>
        {payload.description && (
          <Typography variant="body2" color="text.secondary">
            {payload.description}
          </Typography>
        )}
        <Divider />
        <FieldRow label="Dates" value={`${payload.startDate} - ${payload.endDate}`} />
        <FieldRow label="Timezone" value={payload.timezone} />
        <FieldRow label="Status" value={payload.status} />
        {payload.venueLabel && (
          <FieldRow
            label="Venue"
            value={
              payload.venueAddress
                ? `${payload.venueLabel} (${payload.venueAddress})`
                : payload.venueLabel
            }
          />
        )}
        {payload.heroImage && (
          <FieldRow label="Hero image" value={payload.heroImage} />
        )}
        {payload.heroTitleOverride && (
          <FieldRow
            label="Hero title override"
            value={payload.heroTitleOverride}
          />
        )}
        {payload.faqs.length > 0 && (
          <>
            <Divider />
            <Typography variant="body2" fontWeight={500}>
              FAQs ({payload.faqs.length})
            </Typography>
            <Stack spacing={1}>
              {payload.faqs.map((entry, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {entry.question}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {entry.answer}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          </>
        )}
        {resolution.message && (
          <Typography variant="caption" color="warning.main">
            {resolution.message}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

function FieldRow({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) {
  return (
    <Stack direction="row" spacing={1}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: 96 }}
      >
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Stack>
  );
}

function StatusChip({
  status,
}: {
  status: BulkResolveEventsRowResult["status"];
}) {
  switch (status) {
    case "new":
      return <Chip size="small" label="New, ready" color="success" />;
    case "matches_existing":
      return (
        <Chip size="small" label="Name already exists" color="warning" />
      );
    case "failed":
      return <Chip size="small" label="Failed" color="error" />;
  }
}

function assertHeadersMatch(
  actualHeaders: string[],
  expectedHeaders: string[],
  sheetName: string,
): void {
  const actual = actualHeaders.map((h) => h.trim());
  const expected = expectedHeaders.map((h) => h.trim());
  const exact =
    actual.length === expected.length &&
    actual.every((header, index) => header === expected[index]);
  if (exact) return;
  throw new Error(
    `Sheet "${sheetName}" headers do not match the import template. ` +
      `Expected exactly: ${expected.join(", ")}. ` +
      `Found: ${actual.filter(Boolean).join(", ") || "(none)"}.`,
  );
}

function buildEventBuckets(
  writeResult: BulkCreateEventsResponse,
  review: ReviewState | null,
): BulkImportResultBucket[] {
  const success: BulkImportResultBucket = {
    id: "success",
    label: "Created",
    color: "success",
    description: "Event saved. Open it from the Events list to wire up sessions, audiences, and roster.",
    rows: [],
  };
  const duplicates: BulkImportResultBucket = {
    id: "duplicate",
    label: "Already Exists",
    color: "warning",
    description: "An event with this name already exists. Existing event was kept.",
    rows: [],
  };
  const failed: BulkImportResultBucket = {
    id: "failed",
    label: "Failed",
    color: "error",
    rows: [],
  };

  for (const r of writeResult.results) {
    const summaryRow = {
      id: `r-${r.index}`,
      primary: r.name,
      secondary: r.message ?? undefined,
    };
    if (r.status === "created") {
      success.rows.push({
        ...summaryRow,
        secondary: review?.payload.venueLabel
          ? `Venue: ${review.payload.venueLabel}`
          : undefined,
      });
    } else if (r.status === "duplicate_name") {
      duplicates.rows.push(summaryRow);
    } else {
      failed.rows.push(summaryRow);
    }
  }

  return [success, duplicates, failed];
}
